/**
 * TravelDetailPage — /travel/:type/:slug
 *
 * Full profile for a single travel operator. Renders operator meta
 * (name, code, location, website) and — if linked — the AI-authored
 * article content from sp_articles. Admins can generate a fresh
 * article via Claude Sonnet 4.5.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Plane, Building2, Train, Bus, Ship, Anchor, Car, ArrowLeft, MapPin, ExternalLink, Sparkles, Loader2, Globe, Tag,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import TravelCategoryNav from "@/components/TravelCategoryNav";
import PhotoSubmitModal from "@/components/PhotoSubmitModal";
import ContentPhotoStrip from "@/components/ContentPhotoStrip";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICON = {
  airline: Plane, airport: Building2, rail_operator: Train, rail_station: Train,
  bus_terminal: Bus, boat_terminal: Ship, seaport: Anchor, taxi_service: Car,
};

export default function TravelDetailPage() {
  const { type: slugType, slug } = useParams();
  const { user, token } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [generating, setGenerating] = useState(false);

  const load = () => {
    setState(s => ({ ...s, loading: true }));
    axios.get(`${API}/api/travel/operators/${slug}`)
      .then(r => setState({ loading: false, data: r.data, error: null }))
      .catch(e => setState({ loading: false, data: null, error: e?.response?.data?.detail || "Not found" }));
  };
  useEffect(load, [slug]);

  const isAdmin = user?.role === "admin";

  const generate = async () => {
    if (!token || !isAdmin) return;
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/api/travel/operators/${state.data.operator.id}/generate-article`,
        { style: "encyclopedia" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Article generated!");
      // Reload with the fresh article
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center opacity-60">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
        Loading…
      </div>
    );
  }
  if (state.error || !state.data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-3xl mb-2">Operator not found</h1>
        <Link to={`/travel/${slugType}`} className="wofy-link">← Back</Link>
      </div>
    );
  }

  const { operator, article, nearby } = state.data;
  const Icon = TYPE_ICON[operator.type] || Plane;
  const location = [operator.city, operator.state, operator.country].filter(Boolean).join(", ");

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid={`travel-detail-${operator.slug}`}>
      <TravelCategoryNav />
      <Link to={`/travel/${slugType}`} className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4 mt-4" data-testid="back-to-list">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      {/* Photo submit CTA — sticky top-right on wide screens */}
      <div className="flex justify-end mb-3">
        <PhotoSubmitModal
          targetType="travel_operator"
          targetSlug={operator.slug}
          targetId={operator.id}
          targetTitle={operator.name}
        />
      </div>

      {/* Hero */}
      <section className="rounded-xl overflow-hidden mb-6 border" style={{ borderColor: "var(--wofy-border)" }}>
        {operator.photo_url && (
          <img src={operator.photo_url} alt={operator.name} className="w-full h-56 object-cover" />
        )}
        <div className="p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-xl grid place-items-center text-white flex-shrink-0" style={{ background: "var(--wofy-primary)" }}>
              <Icon className="w-8 h-8" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="overline text-xs opacity-70">{operator.type.replace("_", " ").toUpperCase()}</div>
              <h1 className="font-display text-3xl sm:text-4xl leading-tight" data-testid="operator-name">{operator.name}</h1>
              <div className="mt-2 flex items-center gap-3 flex-wrap text-sm opacity-80">
                {operator.code && (
                  <span className="font-mono px-2 py-0.5 rounded" style={{ background: "var(--wofy-surface)" }}>{operator.code}</span>
                )}
                {location && (
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{location}</span>
                )}
                {operator.website && (
                  <a href={operator.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 wofy-link" data-testid="operator-website">
                    <Globe className="w-3.5 h-3.5" />{operator.website.replace(/^https?:\/\//, "").slice(0, 40)}<ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {operator.tags && operator.tags.length > 0 && (
                <div className="mt-3 flex gap-1.5 flex-wrap">
                  {operator.tags.slice(0, 6).map(t => (
                    <span key={t} className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "var(--wofy-surface)" }}>
                      <Tag className="w-2.5 h-2.5" />{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {operator.description && (
            <p className="mt-4 opacity-90 leading-relaxed">{operator.description}</p>
          )}
        </div>
      </section>

      {/* Article body */}
      {article ? (
        <section className="wofy-card !p-6 mb-6" data-testid="operator-article">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
            <h2 className="font-display text-xl m-0">Travel Guide</h2>
            <span className="text-xs opacity-60">by {article.author}</span>
          </div>
          {/* Top photos */}
          <ContentPhotoStrip targetType="travel_operator" targetSlug={operator.slug} slot="top" max={4} />
          <article
            className="prose prose-sm max-w-none prose-headings:font-display prose-headings:mt-6"
            dangerouslySetInnerHTML={{ __html: article.content_html }}
          />
          {/* Middle & bottom photos */}
          <ContentPhotoStrip targetType="travel_operator" targetSlug={operator.slug} slot="middle" max={4} />
          <ContentPhotoStrip targetType="travel_operator" targetSlug={operator.slug} slot="bottom" max={4} />
          {article.source_url && (
            <div className="mt-4 pt-4 text-xs opacity-60 border-t" style={{ borderColor: "var(--wofy-border)" }}>
              Source: <a href={article.source_url} target="_blank" rel="noreferrer" className="wofy-link">{article.source_url}</a>
            </div>
          )}
        </section>
      ) : (
        <section className="wofy-card !p-6 mb-6 text-center">
          <Sparkles className="w-8 h-8 mx-auto opacity-40 mb-2" />
          <h2 className="font-display text-xl mb-1">No travel guide yet</h2>
          <p className="text-sm opacity-70 mb-3">Detailed AI-authored profile hasn't been generated for this listing.</p>
          {isAdmin && (
            <button
              onClick={generate}
              disabled={generating}
              className="wofy-btn-primary inline-flex items-center gap-2"
              data-testid="generate-article-btn"
            >
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" />Generating…</> : <><Sparkles className="w-4 h-4" />Generate with AI</>}
            </button>
          )}
          {!isAdmin && (
            <p className="text-xs opacity-60">An admin can generate one from the AI editorial tools.</p>
          )}
        </section>
      )}

      {/* Nearby / same country */}
      {nearby && nearby.length > 0 && (
        <section className="wofy-card !p-5">
          <h2 className="font-display text-lg mb-3">Other {operator.type.replace("_", " ")}s in {operator.country || "the region"}</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {nearby.map(n => (
              <Link key={n.id} to={`/travel/${slugType}/${n.slug}`} className="block p-3 rounded-lg border hover:shadow-md transition" style={{ borderColor: "var(--wofy-border)" }} data-testid={`nearby-${n.slug}`}>
                <div className="flex items-center gap-3">
                  {n.photo_url ? (
                    <img src={n.photo_url} alt="" className="w-10 h-10 rounded-md object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-md grid place-items-center" style={{ background: "var(--wofy-surface)" }}>
                      <Icon className="w-4 h-4 opacity-60" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium leading-tight truncate">{n.name}</div>
                    <div className="text-xs opacity-70">{n.code && <span className="font-mono mr-1">{n.code}</span>}{[n.city, n.state].filter(Boolean).join(", ")}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
